/**
 * Webmail admin — layout 3 colonnes (folders / liste / preview+compose).
 *
 * Inspiré de `pages/AdminMail.tsx` (Sakinea), adapté React Router + Tailwind.
 * Connecté au router FastAPI /api/admin/mail/*.
 *
 * Real-time push via SSE (event "new-mail") → re-fetch automatique.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useConfirm } from "@/components/admin/ConfirmProvider";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Inbox as InboxIcon,
    Send,
    Trash2,
    FileEdit,
    AlertOctagon,
    RefreshCw,
    PenSquare,
    Reply,
    ArrowLeft,
    X,
    Paperclip,
    Mail as MailIcon,
    Settings as SettingsIcon,
    CheckCircle2,
    XCircle,
    Loader2,
} from "lucide-react";

const FOLDER_ICONS = {
    inbox: InboxIcon,
    sent: Send,
    trash: Trash2,
    drafts: FileEdit,
    junk: AlertOctagon,
};

function _extractError(e) {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        // FastAPI validation errors: [{type, loc, msg, ...}, ...]
        return detail.map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d))).join(" · ");
    }
    if (detail && typeof detail === "object") {
        return detail.msg || JSON.stringify(detail);
    }
    return e?.message || "Erreur inconnue";
}

function fmtDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const today = new Date();
        const sameDay =
            d.getFullYear() === today.getFullYear() &&
            d.getMonth() === today.getMonth() &&
            d.getDate() === today.getDate();
        return sameDay
            ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
            : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
    } catch {
        return "";
    }
}

export default function AdminMail() {
    const { user } = useAuth();
    const confirm = useConfirm();
    const [folders, setFolders] = useState([]);
    const [currentFolder, setCurrentFolder] = useState("inbox");
    const [messages, setMessages] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [selected, setSelected] = useState(null); // détail message
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [composeOpen, setComposeOpen] = useState(false);
    const [composeData, setComposeData] = useState({
        to: "", cc: "", subject: "", body: "", in_reply_to: null, references: [],
    });
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const sseRef = useRef(null);

    // --------- Fetchers ----------
    async function loadFolders() {
        try {
            const { data } = await api.get("/admin/mail/folders");
            setFolders(data || []);
            setError("");
        } catch (e) {
            setError(_extractError(e));
        }
    }

    async function loadMessages(folder = currentFolder) {
        setLoadingList(true);
        try {
            const { data } = await api.get(`/admin/mail/folders/${folder}/messages`);
            setMessages(data || []);
            setError("");
        } catch (e) {
            setError(_extractError(e));
            setMessages([]);
        } finally {
            setLoadingList(false);
        }
    }

    async function openMessage(msg) {
        setLoadingDetail(true);
        try {
            const { data } = await api.get(`/admin/mail/folders/${currentFolder}/messages/${msg.uid}`);
            setSelected(data);
            // Mark read in liste
            setMessages((m) => m.map((x) => (x.uid === msg.uid ? { ...x, isRead: true } : x)));
            loadFolders();  // refresh unread counts
        } catch (e) {
            toast.error("Impossible d'ouvrir le message");
        } finally {
            setLoadingDetail(false);
        }
    }

    async function toggleRead(uid, read) {
        try {
            await api.post(`/admin/mail/messages/${currentFolder}/${uid}/read`, { read });
            setMessages((m) => m.map((x) => (x.uid === uid ? { ...x, isRead: read } : x)));
            loadFolders();
        } catch (_) {
            toast.error("Échec mise à jour");
        }
    }

    async function moveToTrash(uid) {
        if (!(await confirm({ title: "Supprimer ce message ?", message: "Le message sera déplacé vers la corbeille.", confirmLabel: "Supprimer" }))) return;
        try {
            await api.delete(`/admin/mail/messages/${currentFolder}/${uid}`);
            toast.success("Message supprimé");
            setSelected(null);
            loadMessages();
            loadFolders();
        } catch (_) {
            toast.error("Échec suppression");
        }
    }

    function startReply(msg) {
        setComposeData({
            to: msg.from?.address || "",
            cc: "",
            subject: msg.subject?.startsWith("Re:") ? msg.subject : `Re: ${msg.subject || ""}`,
            body: `\n\n\n— Le ${fmtDate(msg.date)}, ${msg.from?.name || msg.from?.address} a écrit :\n${(msg.bodyText || "").split("\n").map((l) => "> " + l).join("\n")}`,
            in_reply_to: msg.messageId || null,
            references: [...(msg.references || []), msg.messageId].filter(Boolean),
        });
        setComposeOpen(true);
    }

    function startNew() {
        setComposeData({ to: "", cc: "", subject: "", body: "", in_reply_to: null, references: [] });
        setComposeOpen(true);
    }

    async function send() {
        if (!composeData.to.trim()) {
            toast.error("Destinataire requis");
            return;
        }
        if (!composeData.subject.trim()) {
            toast.error("Sujet requis");
            return;
        }
        setSending(true);
        try {
            await api.post("/admin/mail/send", {
                to: composeData.to.split(/[,;]\s*/).filter(Boolean),
                cc: composeData.cc ? composeData.cc.split(/[,;]\s*/).filter(Boolean) : null,
                subject: composeData.subject,
                body_text: composeData.body,
                in_reply_to: composeData.in_reply_to,
                references: composeData.references,
            });
            toast.success("Email envoyé");
            setComposeOpen(false);
            if (currentFolder === "sent") loadMessages();
        } catch (e) {
            toast.error("Envoi échoué : " + _extractError(e));
        } finally {
            setSending(false);
        }
    }

    // --------- SSE (push temps réel) ----------
    useEffect(() => {
        try {
            const url = `${api.defaults.baseURL}/admin/mail/sse`;
            const token = localStorage.getItem("em_token");
            // EventSource ne gère pas les en-têtes. Plutôt que d'exposer le token
            // dans l'URL (→ logs serveur, historique), on le dépose dans un cookie
            // limité au seul chemin du flux SSE, que le backend lit déjà
            // (extract_bearer_token → cookie access_token). Le cookie n'est donc
            // envoyé qu'à cet endpoint précis.
            let ssePath = "/api/admin/mail/sse";
            try { ssePath = new URL(url).pathname; } catch (_) {}
            const secure = window.location.protocol === "https:" ? "; Secure" : "";
            if (token) {
                document.cookie = `access_token=${token}; path=${ssePath}; SameSite=Strict${secure}`;
            }
            const es = new EventSource(url, { withCredentials: true });
            es.addEventListener("new-mail", () => {
                if (currentFolder === "inbox") loadMessages("inbox");
                loadFolders();
            });
            es.addEventListener("ready", () => {});
            sseRef.current = es;
            return () => {
                es.close();
                // Efface le cookie à la fermeture (best-effort)
                document.cookie = `access_token=; path=${ssePath}; SameSite=Strict${secure}; Max-Age=0`;
            };
        } catch (_) {
            // SSE non critique
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFolder]);

    // --------- Initial load ----------
    useEffect(() => {
        loadFolders();
        loadMessages("inbox");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSelected(null);
        loadMessages(currentFolder);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFolder]);

    // --------- Render ----------
    if (user?.role !== "admin") {
        return (
            <div className="em-container py-32 text-center">
                <h1 className="font-display text-4xl font-black text-primary">Accès refusé</h1>
            </div>
        );
    }

    return (
        <div className="em-container py-8">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <p className="em-eyebrow">Communications</p>
                    <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">
                        Boîte mail
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setSettingsOpen(true)}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border-2 border-border bg-card text-foreground hover:border-primary/40 transition-colors"
                        title="Réglages mail"
                    >
                        <SettingsIcon className="h-4 w-4" />
                        Réglages
                    </button>
                    <button
                        onClick={() => { loadMessages(); loadFolders(); }}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border-2 border-border bg-card text-foreground hover:border-primary/40 transition-colors"
                        title="Actualiser"
                    >
                        <RefreshCw className="h-4 w-4" />
                    </button>
                    <button
                        onClick={startNew}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90"
                    >
                        <PenSquare className="h-4 w-4" />
                        Nouveau message
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4 mb-4 text-sm">
                    <strong>Mail indisponible :</strong> {error}
                    {/^Connexion IMAP impossible|Envoi impossible/.test(error) && (
                        <span className="text-muted-foreground"> — clique sur <strong>Réglages</strong> en haut à droite pour configurer SMTP/IMAP.</span>
                    )}
                </div>
            )}

            {settingsOpen && (
                <SettingsModal
                    onClose={() => setSettingsOpen(false)}
                    onSaved={() => { setSettingsOpen(false); loadFolders(); loadMessages(); }}
                />
            )}

            {/* Layout 3 colonnes */}
            <div className="grid grid-cols-12 gap-4 min-h-[60vh]">
                {/* Col 1 : dossiers */}
                <aside className="col-span-12 md:col-span-2 rounded-2xl border border-border bg-card p-2">
                    <ul className="space-y-1">
                        {folders.map((f) => {
                            const Icon = FOLDER_ICONS[f.key] || MailIcon;
                            const active = f.key === currentFolder;
                            return (
                                <li key={f.key}>
                                    <button
                                        onClick={() => setCurrentFolder(f.key)}
                                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                            active
                                                ? "bg-primary text-primary-foreground"
                                                : "text-foreground/80 hover:bg-muted"
                                        }`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <Icon className="h-4 w-4" />
                                            {f.label}
                                        </span>
                                        {f.unread > 0 && (
                                            <span className={`rounded-full px-2 text-xs font-semibold ${
                                                active ? "bg-primary-foreground/20" : "bg-primary/10 text-primary"
                                            }`}>
                                                {f.unread}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </aside>

                {/* Col 2 : liste de messages */}
                <section className="col-span-12 md:col-span-4 rounded-2xl border border-border bg-card overflow-hidden">
                    <div className="border-b border-border p-3 flex items-center justify-between">
                        <span className="text-sm font-semibold text-foreground/80">
                            {folders.find((f) => f.key === currentFolder)?.label || currentFolder}
                        </span>
                        <span className="text-xs text-muted-foreground">{messages.length}</span>
                    </div>
                    {loadingList ? (
                        <p className="p-6 text-sm text-muted-foreground text-center">Chargement…</p>
                    ) : messages.length === 0 ? (
                        <p className="p-6 text-sm text-muted-foreground text-center">Aucun message</p>
                    ) : (
                        <ul className="divide-y divide-border max-h-[70vh] overflow-y-auto">
                            {messages.map((m) => (
                                <li
                                    key={m.uid}
                                    onClick={() => openMessage(m)}
                                    className={`p-3 cursor-pointer hover:bg-muted/50 ${
                                        selected?.uid === m.uid ? "bg-muted" : ""
                                    } ${!m.isRead ? "font-semibold" : ""}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm truncate text-foreground">
                                            {m.from?.name || m.from?.address || "(inconnu)"}
                                        </span>
                                        <span className="text-xs text-muted-foreground shrink-0">
                                            {fmtDate(m.date)}
                                        </span>
                                    </div>
                                    <p className="text-sm text-foreground/80 truncate mt-0.5">
                                        {m.subject || "(sans sujet)"}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Col 3 : détail / compose */}
                <article className="col-span-12 md:col-span-6 rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
                    {composeOpen ? (
                        <Compose
                            data={composeData}
                            setData={setComposeData}
                            onCancel={() => setComposeOpen(false)}
                            onSend={send}
                            sending={sending}
                        />
                    ) : loadingDetail ? (
                        <p className="p-6 text-sm text-muted-foreground text-center">Chargement…</p>
                    ) : selected ? (
                        <Detail
                            message={selected}
                            onReply={() => startReply(selected)}
                            onDelete={() => moveToTrash(selected.uid)}
                            onClose={() => setSelected(null)}
                            onToggleRead={(read) => toggleRead(selected.uid, read)}
                        />
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm p-8">
                            Sélectionne un message à gauche
                        </div>
                    )}
                </article>
            </div>
        </div>
    );
}


function Detail({ message, onReply, onDelete, onClose, onToggleRead }) {
    return (
        <>
            <header className="p-4 border-b border-border flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="font-display text-xl font-bold text-primary truncate">
                        {message.subject || "(sans sujet)"}
                    </h2>
                    <p className="text-sm text-foreground/80 mt-1">
                        <span className="font-medium">{message.from?.name || message.from?.address}</span>{" "}
                        <span className="text-muted-foreground">&lt;{message.from?.address}&gt;</span>
                    </p>
                    {message.to?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            À : {message.to.map((a) => a.address).join(", ")}
                        </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">{fmtDate(message.date)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={onReply}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm border border-border hover:bg-muted"
                    >
                        <Reply className="h-3.5 w-3.5" /> Répondre
                    </button>
                    <button
                        onClick={() => onToggleRead(!message.isRead)}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm border border-border hover:bg-muted"
                        title={message.isRead ? "Marquer non lu" : "Marquer lu"}
                    >
                        <MailIcon className="h-3.5 w-3.5" />
                    </button>
                    <button
                        onClick={onDelete}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm border border-border text-destructive hover:bg-destructive/10"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full hover:bg-muted"
                        title="Fermer"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </header>
            <div className="p-4 overflow-y-auto flex-1">
                {message.bodyHtml ? (
                    /* Le HTML d'un email vient de l'extérieur : on l'isole dans une
                       iframe sandbox SANS allow-scripts → tout JS (et donc toute
                       tentative XSS) est neutralisé par le navigateur. */
                    <iframe
                        title="Contenu de l'email"
                        sandbox=""
                        referrerPolicy="no-referrer"
                        srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><meta name="referrer" content="no-referrer"><style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#1a1a1a;margin:0;padding:4px;word-wrap:break-word;overflow-wrap:break-word}img{max-width:100%;height:auto}a{color:#0a5c3a}</style></head><body>${message.bodyHtml}</body></html>`}
                        className="w-full min-h-[40vh] rounded-lg border border-border bg-white"
                    />
                ) : (
                    <pre className="text-sm whitespace-pre-wrap font-sans text-foreground/90">
                        {message.bodyText || "(corps vide)"}
                    </pre>
                )}
                {message.attachments?.length > 0 && (
                    <div className="mt-6 border-t border-border pt-4">
                        <p className="em-eyebrow flex items-center gap-2 mb-3">
                            <Paperclip className="h-3.5 w-3.5" />
                            {message.attachments.length} pièce{message.attachments.length > 1 ? "s" : ""} jointe{message.attachments.length > 1 ? "s" : ""}
                        </p>
                        <ul className="space-y-2">
                            {message.attachments.map((a, i) => (
                                <li key={i} className="text-sm flex items-center gap-2 text-foreground/80">
                                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                                    {a.filename}
                                    <span className="text-xs text-muted-foreground">
                                        ({Math.round(a.size / 1024)} KB)
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </>
    );
}


function Compose({ data, setData, onCancel, onSend, sending }) {
    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
    return (
        <>
            <header className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-display text-xl font-bold text-primary">
                    {data.in_reply_to ? "Réponse" : "Nouveau message"}
                </h2>
                <button onClick={onCancel} className="p-1.5 rounded-full hover:bg-muted">
                    <ArrowLeft className="h-4 w-4" />
                </button>
            </header>
            <div className="p-4 space-y-3 flex-1 flex flex-col">
                <div>
                    <label className="text-xs font-semibold text-muted-foreground">À</label>
                    <input
                        type="text"
                        value={data.to}
                        onChange={(e) => set("to", e.target.value)}
                        placeholder="exemple@domain.fr, autre@domain.fr"
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs font-semibold text-muted-foreground">Cc</label>
                    <input
                        type="text"
                        value={data.cc}
                        onChange={(e) => set("cc", e.target.value)}
                        placeholder="(optionnel)"
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs font-semibold text-muted-foreground">Sujet</label>
                    <input
                        type="text"
                        value={data.subject}
                        onChange={(e) => set("subject", e.target.value)}
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    />
                </div>
                <div className="flex-1 flex flex-col">
                    <label className="text-xs font-semibold text-muted-foreground">Message</label>
                    <textarea
                        value={data.body}
                        onChange={(e) => set("body", e.target.value)}
                        className="mt-1 w-full flex-1 min-h-[200px] rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono resize-none"
                    />
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                        onClick={onCancel}
                        className="rounded-full px-4 py-2 text-sm border border-border hover:bg-muted"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={onSend}
                        disabled={sending}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                        <Send className="h-4 w-4" />
                        {sending ? "Envoi…" : "Envoyer"}
                    </button>
                </div>
            </div>
        </>
    );
}


function SettingsModal({ onClose, onSaved }) {
    const [form, setForm] = useState({
        SMTP_HOST: "", SMTP_PORT: "465", SMTP_SECURE: "true",
        SMTP_USER: "", SMTP_PASS: "",
        SMTP_FROM_NAME: "", SMTP_FROM_EMAIL: "",
        IMAP_HOST: "", IMAP_PORT: "993", IMAP_SECURE: "true",
        IMAP_USER: "", IMAP_PASS: "",
        IMAP_FOLDER_SENT: "", IMAP_FOLDER_TRASH: "",
        IMAP_FOLDER_DRAFTS: "", IMAP_FOLDER_JUNK: "",
    });
    const [hasSmtpPass, setHasSmtpPass] = useState(false);
    const [hasImapPass, setHasImapPass] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/mail/settings");
                setForm((f) => ({
                    ...f,
                    SMTP_HOST: data.SMTP_HOST || "",
                    SMTP_PORT: data.SMTP_PORT || "465",
                    SMTP_SECURE: data.SMTP_SECURE || "true",
                    SMTP_USER: data.SMTP_USER || "",
                    SMTP_FROM_NAME: data.SMTP_FROM_NAME || "",
                    SMTP_FROM_EMAIL: data.SMTP_FROM_EMAIL || "",
                    IMAP_HOST: data.IMAP_HOST || "",
                    IMAP_PORT: data.IMAP_PORT || "993",
                    IMAP_SECURE: data.IMAP_SECURE || "true",
                    IMAP_USER: data.IMAP_USER || "",
                    IMAP_FOLDER_SENT: data.IMAP_FOLDER_SENT || "",
                    IMAP_FOLDER_TRASH: data.IMAP_FOLDER_TRASH || "",
                    IMAP_FOLDER_DRAFTS: data.IMAP_FOLDER_DRAFTS || "",
                    IMAP_FOLDER_JUNK: data.IMAP_FOLDER_JUNK || "",
                }));
                setHasSmtpPass(!!data.hasSmtpPass);
                setHasImapPass(!!data.hasImapPass);
            } catch (e) {
                toast.error("Lecture config échouée : " + _extractError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function save() {
        setSaving(true);
        try {
            const payload = { ...form };
            // Mots de passe : si vide, on n'envoie PAS pour préserver l'ancien
            if (!payload.SMTP_PASS) delete payload.SMTP_PASS;
            if (!payload.IMAP_PASS) delete payload.IMAP_PASS;
            await api.put("/admin/mail/settings", payload);
            toast.success("Configuration enregistrée");
            onSaved && onSaved();
        } catch (e) {
            toast.error("Échec sauvegarde : " + _extractError(e));
        } finally {
            setSaving(false);
        }
    }

    async function testConnection() {
        setTesting(true);
        setTestResult(null);
        try {
            // On sauve d'abord ce qui est dans le form, puis on teste (sinon test sur ancienne config)
            const payload = { ...form };
            if (!payload.SMTP_PASS) delete payload.SMTP_PASS;
            if (!payload.IMAP_PASS) delete payload.IMAP_PASS;
            await api.put("/admin/mail/settings", payload);
            const { data } = await api.post("/admin/mail/settings/test");
            setTestResult(data);
        } catch (e) {
            toast.error("Test échoué : " + _extractError(e));
        } finally {
            setTesting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-background w-full max-w-3xl rounded-3xl shadow-2xl my-8 overflow-hidden">
                <header className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] opacity-80">Réglages</p>
                        <h2 className="font-display text-2xl font-bold mt-1">Boîte mail — SMTP & IMAP</h2>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                {loading ? (
                    <div className="p-12 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
                    </div>
                ) : (
                    <div className="p-6 space-y-6">
                        {/* SMTP */}
                        <section>
                            <h3 className="font-display text-lg font-bold text-primary mb-3 flex items-center gap-2">
                                <Send className="h-4 w-4" /> SMTP — envoi
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Serveur SMTP" value={form.SMTP_HOST} onChange={(v) => set("SMTP_HOST", v)} placeholder="ssl0.ovh.net" />
                                <Field label="Port" value={form.SMTP_PORT} onChange={(v) => set("SMTP_PORT", v)} placeholder="465" />
                                <Field label="Identifiant" value={form.SMTP_USER} onChange={(v) => set("SMTP_USER", v)} placeholder="contact@energymarket.fr" />
                                <Field
                                    label="Mot de passe"
                                    type="password"
                                    value={form.SMTP_PASS}
                                    onChange={(v) => set("SMTP_PASS", v)}
                                    placeholder={hasSmtpPass ? "•••••••• (laisser vide pour conserver)" : ""}
                                />
                                <Field label="Nom d'expéditeur" value={form.SMTP_FROM_NAME} onChange={(v) => set("SMTP_FROM_NAME", v)} placeholder="Energy Market" />
                                <Field label="Email d'expéditeur" value={form.SMTP_FROM_EMAIL} onChange={(v) => set("SMTP_FROM_EMAIL", v)} placeholder="contact@energymarket.fr" />
                                <SelectField
                                    label="SSL/TLS"
                                    value={form.SMTP_SECURE}
                                    onChange={(v) => set("SMTP_SECURE", v)}
                                    options={[{ value: "true", label: "Activé (port 465)" }, { value: "false", label: "Désactivé / STARTTLS (port 587)" }]}
                                />
                            </div>
                        </section>

                        {/* IMAP */}
                        <section>
                            <h3 className="font-display text-lg font-bold text-primary mb-3 flex items-center gap-2">
                                <InboxIcon className="h-4 w-4" /> IMAP — réception
                            </h3>
                            <p className="text-xs text-muted-foreground mb-3">
                                Si laissé vide, IMAP_USER/IMAP_PASS reprennent les valeurs SMTP.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Serveur IMAP" value={form.IMAP_HOST} onChange={(v) => set("IMAP_HOST", v)} placeholder="ssl0.ovh.net" />
                                <Field label="Port" value={form.IMAP_PORT} onChange={(v) => set("IMAP_PORT", v)} placeholder="993" />
                                <Field label="Identifiant" value={form.IMAP_USER} onChange={(v) => set("IMAP_USER", v)} placeholder="(reprend SMTP si vide)" />
                                <Field
                                    label="Mot de passe"
                                    type="password"
                                    value={form.IMAP_PASS}
                                    onChange={(v) => set("IMAP_PASS", v)}
                                    placeholder={hasImapPass ? "•••••••• (laisser vide pour conserver)" : "(reprend SMTP si vide)"}
                                />
                                <SelectField
                                    label="SSL"
                                    value={form.IMAP_SECURE}
                                    onChange={(v) => set("IMAP_SECURE", v)}
                                    options={[{ value: "true", label: "Activé (port 993)" }, { value: "false", label: "Désactivé (port 143)" }]}
                                />
                            </div>
                        </section>

                        {/* Dossiers (avancé) */}
                        <details className="border border-border rounded-xl">
                            <summary className="cursor-pointer p-3 text-sm font-semibold">
                                Avancé : noms de dossiers IMAP
                            </summary>
                            <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-border">
                                <p className="md:col-span-2 text-xs text-muted-foreground">
                                    Auto-découverte via SPECIAL-USE par défaut. Override uniquement si ton serveur n'utilise pas les noms standards.
                                </p>
                                <Field label="Envoyés" value={form.IMAP_FOLDER_SENT} onChange={(v) => set("IMAP_FOLDER_SENT", v)} placeholder="Sent" />
                                <Field label="Corbeille" value={form.IMAP_FOLDER_TRASH} onChange={(v) => set("IMAP_FOLDER_TRASH", v)} placeholder="Trash" />
                                <Field label="Brouillons" value={form.IMAP_FOLDER_DRAFTS} onChange={(v) => set("IMAP_FOLDER_DRAFTS", v)} placeholder="Drafts" />
                                <Field label="Spam" value={form.IMAP_FOLDER_JUNK} onChange={(v) => set("IMAP_FOLDER_JUNK", v)} placeholder="Junk" />
                            </div>
                        </details>

                        {/* Résultat du test */}
                        {testResult && (
                            <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                                <TestRow label="SMTP" ok={testResult.smtp?.ok} error={testResult.smtp?.error} />
                                <TestRow label="IMAP" ok={testResult.imap?.ok} error={testResult.imap?.error} />
                            </div>
                        )}
                    </div>
                )}

                <footer className="border-t border-border p-4 flex items-center justify-between gap-3">
                    <button
                        onClick={testConnection}
                        disabled={testing || loading}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border-2 border-border bg-card hover:border-primary/40 disabled:opacity-50"
                    >
                        {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        Tester la connexion
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="rounded-full px-4 py-2 text-sm border border-border hover:bg-muted"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={save}
                            disabled={saving || loading}
                            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            Enregistrer
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}


function Field({ label, value, onChange, type = "text", placeholder }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
        </label>
    );
}


function SelectField({ label, value, onChange, options }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}


function TestRow({ label, ok, error }) {
    return (
        <div className="flex items-start gap-2 text-sm">
            {ok ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
            ) : (
                <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            )}
            <div>
                <span className="font-semibold">{label}</span>
                {ok ? (
                    <span className="text-muted-foreground"> · Connexion OK</span>
                ) : (
                    <span className="text-destructive"> · {error || "Échec"}</span>
                )}
            </div>
        </div>
    );
}
